<script setup lang="ts">
/**
 * Integrationen als Bento: Stripe als Featured-Zelle (2 Spalten, Akzent-Tint
 * + abstrahierte Bot-Nachricht im Ticket als Preview), PayPal daneben, darunter drei
 * kompakte Zellen, Webhooks als abweichender Full-Width-Streifen mit
 * Event-Chips. Sechs Inhalte, sechs Zellen — keine Leerkachel, keine
 * monotone 3×2-Wiederholung.
 *
 * Entrance: Headline/Intro über useReveal; das Bento selbst erzählt seinen
 * Inhalt — Zellen in Leserichtung, Glyphen drehen ein, die Stripe-Karte im
 * Ticket „lädt" ihre Zeilen nacheinander, die Webhook-Events ploppen als Chips.
 */

const sectionRef = ref<HTMLElement | null>(null)
useReveal(sectionRef)
useSpotlight(sectionRef)

useEntrance(sectionRef, ({ q, tl }) => {
  tl.from(q('.bento-cell'), {
    opacity: 0,
    y: 28,
    scale: 0.985,
    duration: 0.8,
    stagger: 0.09,
    clearProps: 'transform'
  }, 0)
  tl.from(q('.integration-icon-glyph'), {
    opacity: 0,
    scale: 0.5,
    rotate: -18,
    duration: 0.6,
    ease: 'back.out(1.8)',
    stagger: 0.09,
    clearProps: 'transform'
  }, 0.25)
  tl.from(q('.stripe-peek .peek-row'), { opacity: 0, x: -12, duration: 0.5, stagger: 0.14, clearProps: 'transform' }, 0.5)
  tl.from(q('.stripe-peek .peek-action'), { opacity: 0, scale: 0.8, duration: 0.4, ease: 'back.out(2)', stagger: 0.1, clearProps: 'transform' }, 0.95)
  tl.from(q('.bento-hooks-event'), {
    opacity: 0,
    scale: 0.7,
    duration: 0.45,
    ease: 'back.out(2)',
    stagger: 0.08,
    clearProps: 'transform'
  }, 0.75)
}, { trigger: '.integrations-bento' })

const compactIntegrations = [
  {
    name: 'Tebex',
    icon: 'i-lucide-shopping-cart',
    benefit: 'Look up payments and subscriptions, refund, pause or cancel them, and create checkouts without leaving the ticket.'
  },
  {
    name: 'Patreon',
    icon: 'i-simple-icons-patreon',
    benefit: 'The member’s tier appears on the ticket, matched through their linked Discord account. Nobody has to ask.'
  },
  {
    name: 'GitHub',
    icon: 'i-simple-icons-github',
    benefit: 'Turn a bug report into a GitHub issue with /issue, using your repository’s own issue templates.'
  }
]

/* Die echten Event-Namen aus dem `X-Clarify-Event`-Header */
const webhookEvents = ['created', 'closed', 'assigned', 'rated', 'escalated']

/* Die Stripe-Karte, wie der Bot sie in den Ticket-Thread stellt */
const stripeRows = [
  { key: 'Status', value: 'succeeded · €9.99' },
  { key: 'Customer', value: 'jonas@mail.de' },
  { key: 'Product', value: 'Premium (1 month)' }
]
</script>

<template>
  <section id="integrationen" ref="sectionRef" class="integrations">
    <span class="section-rule" aria-hidden="true" />
    <div class="section-shell">
      <div class="section-head">
        <h2 class="h-section" data-split>Reply with context.</h2>
        <p class="copy-muted integrations-intro reveal">
          Add an integration’s ready-made input to a category and the member’s order,
          subscription or membership is on the ticket the moment it opens. Lookups,
          refunds and payment links run as slash commands, right inside Discord.
        </p>
      </div>

      <div class="integrations-bento">
        <!-- Featured: Stripe mit Preview der Ticket-Karte -->
        <article class="card-shell bento-cell bento-stripe spot-card entrance">
          <span class="cell-plus cell-plus-tl" aria-hidden="true" />
          <span class="cell-plus cell-plus-br" aria-hidden="true" />
          <span class="bento-stripe-grid" aria-hidden="true" />
          <div class="bento-stripe-copy">
            <span class="integration-icon" aria-hidden="true">
              <UIcon name="i-simple-icons-stripe" class="integration-icon-glyph" />
            </span>
            <h3 class="integration-name">Stripe</h3>
            <p class="integration-benefit copy-muted">
              Payment, customer and subscription are on the ticket as it opens.
              Send invoices and checkout links with <code>/stripe</code>; the card switches to paid as soon as Stripe confirms.
            </p>
          </div>
          <div class="bento-stripe-preview" aria-hidden="true">
            <!-- Abstrahiert: der Bot hat die Karte in den Ticket-Thread gestellt -->
            <PeekMessage
              class="stripe-peek"
              thread="jonas.-Billing"
              title="Stripe Payment"
              accent="#635bff"
              :rows="stripeRows"
              :actions="['View in Stripe', 'Receipt']"
            />
          </div>
        </article>

        <!-- PayPal -->
        <article class="card-shell bento-cell bento-paypal spot-card entrance">
          <span class="integration-icon" aria-hidden="true">
            <UIcon name="i-simple-icons-paypal" class="integration-icon-glyph" />
          </span>
          <h3 class="integration-name">PayPal</h3>
          <p class="integration-benefit copy-muted">
            Create payment links from any ticket with <code>/paypal</code>. Clarify confirms the payment on its own when it arrives.
          </p>
        </article>

        <!-- Drei kompakte Zellen -->
        <article
          v-for="item in compactIntegrations"
          :key="item.name"
          class="card-shell bento-cell spot-card entrance"
        >
          <span class="integration-icon" aria-hidden="true">
            <UIcon :name="item.icon" class="integration-icon-glyph" />
          </span>
          <h3 class="integration-name">{{ item.name }}</h3>
          <p class="integration-benefit copy-muted">{{ item.benefit }}</p>
        </article>

        <!-- Webhooks: bewusst andere Anatomie — schmaler Streifen -->
        <article class="card-shell bento-cell bento-hooks spot-card entrance">
          <div class="bento-hooks-lead">
            <span class="integration-icon" aria-hidden="true">
              <UIcon name="i-lucide-webhook" class="integration-icon-glyph" />
            </span>
            <div class="bento-hooks-copy">
              <h3 class="integration-name">Webhooks</h3>
              <p class="integration-benefit copy-muted">Signed ticket events, sent to your own HTTPS endpoint. Verify the signature and react anywhere in your stack.</p>
            </div>
          </div>
          <div class="bento-hooks-events" aria-hidden="true">
            <code v-for="e in webhookEvents" :key="e" class="bento-hooks-event">{{ e }}</code>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
.integrations {
  position: relative;
  padding: var(--section-gap) 0 0;
}

.integrations-intro {
  max-width: 56ch;
}

/* ── Bento: 3 Spalten, Stripe featured über 2, Webhooks über alle 3 ──────── */
.integrations-bento {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 72px;
}

/* Karten v2: flach, Border trägt die Kante, der Cursor bringt das Licht —
   kein Hover-Lift mehr. */
.bento-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding: 26px 24px;
}

.bento-cell:hover {
  border-color: var(--nb-hairline-strong);
}

/* Featured-Zelle: Akzent-Tint + Preview-Karte */
.bento-stripe {
  flex-direction: column;
  gap: 24px;
  background:
    radial-gradient(120% 130% at 100% 0%, var(--nb-accent-tint), transparent 55%),
    var(--nb-surface);
}

/* Graph-Paper hinter der Preview-Hälfte — organisiert die Featured-Fläche */
.bento-stripe-grid {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  background-image:
    linear-gradient(var(--nb-rail) 1px, transparent 1px),
    linear-gradient(90deg, var(--nb-rail) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: radial-gradient(90% 120% at 82% 50%, #000 20%, transparent 68%);
  -webkit-mask-image: radial-gradient(90% 120% at 82% 50%, #000 20%, transparent 68%);
  pointer-events: none;
}

.bento-stripe-copy,
.bento-stripe-preview {
  position: relative;
}

.bento-stripe-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
}

.bento-stripe-preview {
  width: 100%;
}

.stripe-peek {
  width: 100%;
  max-width: 380px;
}

/* Webhooks-Streifen: horizontale Anatomie, mono Event-Chips */
.bento-hooks {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: var(--nb-surface-soft);
}

.bento-hooks-lead {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.bento-hooks-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.bento-hooks-events {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.bento-hooks-event {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--nb-ink-muted);
  background: var(--nb-bg);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-sm);
  padding: 4px 9px;
  white-space: nowrap;
}

/* Icon-Tile v2: neutraler Verlauf statt lila Chip — der Glyph trägt den Akzent */
.integration-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.01));
  border: 1px solid var(--nb-hairline);
  flex-shrink: 0;
}

.integration-icon-glyph {
  width: 22px;
  height: 22px;
  color: var(--nb-accent-soft);
}

.integration-name {
  font-size: 1.08rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

.integration-benefit {
  font-size: 0.94rem;
  line-height: 1.55;
}

/* Mobile-Kollaps: einspaltig ist oben deklariert; ab sm zwei, ab lg drei */
@media (min-width: 640px) {
  .integrations-bento {
    grid-template-columns: repeat(2, 1fr);
  }

  .bento-stripe,
  .bento-hooks {
    grid-column: span 2;
  }
}

@media (min-width: 900px) {
  .integrations-bento {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .bento-stripe {
    grid-column: span 2;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .bento-stripe-copy {
    max-width: 44%;
  }

  .bento-paypal {
    grid-column: span 1;
  }

  .bento-hooks {
    grid-column: span 3;
  }
}
</style>
